import type {
  GetOpsCustomerScopeContextResponse,
  UpdateAccountSignupBrandResponse,
} from '@nadoutong/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { useSession } from '../hooks/useSession.tsx';
import { opsApiRequest } from './client.ts';
import { CUSTOMER_MANAGER_WORKSPACE_ROOT_KEY } from './customerManagerWorkspace.ts';
import { CUSTOMER_OWNERSHIP_ROOT_KEY } from './customerOwnership.ts';
import { ACCOUNTS_ROOT_KEY } from './useAdmin.ts';

/**
 * Customer brand authority surface (epic #1030 E1-S4, #1040).
 *
 *   - `GET /v1/ops/customers/context` — the server-determined brand/scope
 *     for the session. Pool / workbench / ownership render their brand
 *     chrome from this response; nothing client-side ever decides scope.
 *   - `PUT /v1/ops/admin/accounts/:accountId/signup-brand` — admin-only
 *     brand correction (audited; refused with `SIGNUP_BRAND_CONFLICT`
 *     while a foreign-brand manager actively owns the customer).
 */

export const CUSTOMER_SCOPE_CONTEXT_KEY = ['customer-scope-context'] as const;

export async function getOpsCustomerScopeContext(
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<GetOpsCustomerScopeContextResponse> {
  return opsApiRequest<GetOpsCustomerScopeContextResponse>(
    '/v1/ops/customers/context',
    { signal },
    fetchImpl,
  );
}

export function useOpsCustomerScopeContext(): UseQueryResult<GetOpsCustomerScopeContextResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: CUSTOMER_SCOPE_CONTEXT_KEY,
    queryFn: ({ signal }) => getOpsCustomerScopeContext(fetchImpl, signal),
  });
}

export async function updateAccountSignupBrand(
  accountId: string,
  signupBrand: string,
  fetchImpl: typeof fetch,
): Promise<UpdateAccountSignupBrandResponse> {
  return opsApiRequest<UpdateAccountSignupBrandResponse>(
    `/v1/ops/admin/accounts/${encodeURIComponent(accountId)}/signup-brand`,
    { method: 'PUT', body: { signup_brand: signupBrand } },
    fetchImpl,
  );
}

export interface UpdateAccountSignupBrandVars {
  readonly accountId: string;
  readonly signupBrand: string;
}

export function useUpdateAccountSignupBrand(): UseMutationResult<
  UpdateAccountSignupBrandResponse,
  Error,
  UpdateAccountSignupBrandVars
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ accountId, signupBrand }) =>
      updateAccountSignupBrand(accountId, signupBrand, fetchImpl),
    onSuccess: () => {
      // Brand rides on every customer-facing list row, so refresh the
      // admin accounts cache plus both sales books.
      void qc.invalidateQueries({ queryKey: ACCOUNTS_ROOT_KEY });
      void qc.invalidateQueries({ queryKey: CUSTOMER_OWNERSHIP_ROOT_KEY });
      void qc.invalidateQueries({ queryKey: CUSTOMER_MANAGER_WORKSPACE_ROOT_KEY });
    },
  });
}
